import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Chart from '../components/Chart';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiCalendar, FiFilter, FiShare2, FiFileText, FiBarChart3, FiTrendingUp } = FiIcons;

const Reports = () => {
  const [reportType, setReportType] = useState('executive');
  const [dateRange, setDateRange] = useState('30d');

  const reportTemplates = [
    {
      name: 'Executive Summary',
      description: 'High-level overview of customer feedback and key metrics',
      icon: FiBarChart3,
      color: 'primary',
      lastGenerated: '2 hours ago',
      frequency: 'Weekly'
    },
    {
      name: 'Sentiment Analysis Report',
      description: 'Detailed breakdown of customer sentiment and emotion trends',
      icon: FiTrendingUp,
      color: 'success',
      lastGenerated: '1 day ago',
      frequency: 'Daily'
    },
    {
      name: 'Service Gap Analysis',
      description: 'Identification of service quality gaps and improvement opportunities',
      icon: FiFileText,
      color: 'warning',
      lastGenerated: '3 days ago',
      frequency: 'Monthly'
    },
    {
      name: 'Team Performance Report',
      description: 'Individual and team performance metrics based on customer feedback',
      icon: FiBarChart3,
      color: 'secondary',
      lastGenerated: '1 week ago',
      frequency: 'Bi-weekly'
    }
  ];

  const kpiData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    series: [
      {
        name: 'Customer Satisfaction',
        type: 'line',
        data: [4.1, 4.2, 4.3, 4.2, 4.4, 4.2],
        smooth: true,
        lineStyle: { color: '#22c55e' }
      },
      {
        name: 'Net Promoter Score',
        type: 'line',
        data: [65, 68, 72, 70, 75, 68],
        smooth: true,
        lineStyle: { color: '#0ea5e9' }
      },
      {
        name: 'Response Time (hours)',
        type: 'line',
        data: [3.2, 2.8, 2.5, 2.7, 2.4, 2.4],
        smooth: true,
        lineStyle: { color: '#f59e0b' }
      }
    ]
  };

  const categoryBreakdownData = {
    labels: ['Product', 'Service', 'Support', 'Billing', 'Delivery'],
    series: [
      {
        name: 'Feedback Volume',
        type: 'pie',
        radius: ['40%', '70%'],
        data: [
          { value: 335, name: 'Product', itemStyle: { color: '#0ea5e9' } },
          { value: 310, name: 'Service', itemStyle: { color: '#22c55e' } },
          { value: 234, name: 'Support', itemStyle: { color: '#f59e0b' } },
          { value: 135, name: 'Billing', itemStyle: { color: '#ef4444' } },
          { value: 89, name: 'Delivery', itemStyle: { color: '#8b5cf6' } }
        ]
      }
    ]
  };

  const customReports = [
    {
      name: 'Monthly Customer Health Score',
      created: '2024-01-15',
      parameters: 'All customers, 30-day window',
      status: 'Scheduled',
      nextRun: '2024-02-01'
    },
    {
      name: 'High-Risk Customer Analysis',
      created: '2024-01-10',
      parameters: 'Risk score > 70, Last 7 days',
      status: 'Active',
      nextRun: 'Daily at 9:00 AM'
    },
    {
      name: 'Product Feature Feedback',
      created: '2024-01-05',
      parameters: 'Feature mentions, Sentiment analysis',
      status: 'Paused',
      nextRun: 'Manual'
    }
  ];

  const exportOptions = [
    { label: 'PDF Report', format: 'pdf', description: 'Formatted report with charts and insights' },
    { label: 'Excel Data', format: 'xlsx', description: 'Raw data with pivot tables' },
    { label: 'CSV Export', format: 'csv', description: 'Simple data export for analysis' },
    { label: 'PowerPoint', format: 'pptx', description: 'Presentation-ready slides' }
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Reports & Analytics</h1>
          <p className="text-secondary-600 mt-1">Generate comprehensive reports and export data insights</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <select 
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="custom">Custom Range</option>
          </select>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium flex items-center space-x-2"
          >
            <FiDownload className="w-4 h-4" />
            <span>Generate Report</span>
          </motion.button>
        </div>
      </div>

      {/* Report Templates */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-6">Report Templates</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reportTemplates.map((template, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 border border-secondary-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start space-x-3 mb-3">
                <div className={`w-10 h-10 bg-${template.color}-100 rounded-lg flex items-center justify-center`}>
                  <template.icon className={`w-5 h-5 text-${template.color}-600`} />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-secondary-900">{template.name}</h4>
                  <p className="text-sm text-secondary-600 mt-1">{template.description}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-xs text-secondary-500 mb-3">
                <span>Last generated: {template.lastGenerated}</span>
                <span>{template.frequency}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button className="px-3 py-1 bg-primary-50 text-primary-600 text-sm rounded-lg hover:bg-primary-100 transition-colors">
                  Generate Now
                </button>
                <button className="px-3 py-1 bg-secondary-50 text-secondary-600 text-sm rounded-lg hover:bg-secondary-100 transition-colors">
                  Schedule
                </button>
                <button className="px-3 py-1 bg-secondary-50 text-secondary-600 text-sm rounded-lg hover:bg-secondary-100 transition-colors">
                  Customize
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Chart 
          title="Key Performance Indicators"
          data={kpiData}
          type="line"
          height={400}
        />
        
        <Chart 
          title="Feedback Distribution by Category"
          data={categoryBreakdownData}
          type="pie"
          height={400}
          options={{
            legend: {
              orient: 'vertical',
              left: 'left',
              top: 'center'
            }
          }}
        />
      </div>

      {/* Custom Reports */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-secondary-900">Custom Reports</h3>
          <button className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium">
            Create New Report
          </button>
        </div>
        
        <div className="space-y-4">
          {customReports.map((report, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 border border-secondary-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-secondary-900">{report.name}</h4>
                  <p className="text-sm text-secondary-600">{report.parameters}</p>
                  <p className="text-xs text-secondary-500 mt-1">Created: {report.created}</p>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    report.status === 'Active' 
                      ? 'bg-success-100 text-success-700' 
                      : report.status === 'Scheduled'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {report.status}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <p className="text-sm text-secondary-600">Next run: {report.nextRun}</p>
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-1 bg-primary-50 text-primary-600 text-sm rounded-lg hover:bg-primary-100 transition-colors">
                    Run Now
                  </button>
                  <button className="px-3 py-1 bg-secondary-50 text-secondary-600 text-sm rounded-lg hover:bg-secondary-100 transition-colors">
                    Edit
                  </button>
                  <button className="px-3 py-1 bg-secondary-50 text-secondary-600 text-sm rounded-lg hover:bg-secondary-100 transition-colors">
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Export Options */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-6">Export Options</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {exportOptions.map((option, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 border border-secondary-200 rounded-lg hover:shadow-sm transition-shadow cursor-pointer"
            >
              <div className="flex items-center space-x-3 mb-3">
                <FiDownload className="w-5 h-5 text-primary-600" />
                <h4 className="font-medium text-secondary-900">{option.label}</h4>
              </div>
              <p className="text-sm text-secondary-600 mb-4">{option.description}</p>
              <button className="w-full px-3 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors text-sm font-medium">
                Export as {option.format.toUpperCase()}
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Scheduled Reports */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-secondary-900">Scheduled Reports</h3>
          <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
            Manage Schedules
          </button>
        </div>
        
        <div className="space-y-4">
          {[
            { name: 'Daily Sentiment Summary', time: '9:00 AM', recipients: 'team@trustnation.com', status: 'Active' },
            { name: 'Weekly Performance Report', time: 'Monday 10:00 AM', recipients: 'leadership@trustnation.com', status: 'Active' },
            { name: 'Monthly Executive Summary', time: '1st of month, 8:00 AM', recipients: 'executives@trustnation.com', status: 'Active' }
          ].map((schedule, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-between p-4 border border-secondary-200 rounded-lg"
            >
              <div>
                <h4 className="font-medium text-secondary-900">{schedule.name}</h4>
                <p className="text-sm text-secondary-600">{schedule.time} • {schedule.recipients}</p>
              </div>
              
              <div className="flex items-center space-x-3">
                <span className="px-2 py-1 bg-success-100 text-success-700 text-xs rounded-full">
                  {schedule.status}
                </span>
                <button className="text-secondary-400 hover:text-secondary-600 transition-colors">
                  <FiShare2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Reports;